import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, ArrowRight, Eye, EyeOff } from "lucide-react";
import SendVertexLogo from "@/components/brand/SendVertexLogo";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { AUTH } from "@/constants/testIds";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/app");
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Invalid email or password";
      toast.error(msg, { description: "Please check your credentials and try again." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-slate-950 text-white relative overflow-hidden">
        <div className="absolute inset-0 terminal-grid opacity-40" />
        <div className="relative">
          <Link to="/" className="flex items-center gap-2">
            <SendVertexLogo size={38} wordmarkClassName="text-lg tracking-tight" variant="dark" />
          </Link>
        </div>
        <div className="relative">
          <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-emerald-400 mb-4">// the email engine</div>
          <h2 className="font-display text-4xl font-black tracking-tighter leading-[1.05]">
            Welcome back to<br />the control room.
          </h2>
          <p className="text-slate-300 mt-4 max-w-md">Build, verify, and deliver high-volume email with deliverability built in from the start.</p>
        </div>
        <div className="relative font-mono text-xs text-slate-500">SOC 2 · GDPR · ISO 27001 ready</div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8 flex items-center gap-2">
            <SendVertexLogo size={38} wordmarkClassName="text-lg tracking-tight" />
          </div>
          <div className="text-overline mb-2">Sign in</div>
          <h1 className="font-display text-3xl font-black tracking-tighter text-slate-950">Log in to your account.</h1>
          <p className="text-sm text-slate-500 mt-2">No account yet? <Link to="/register" data-testid={AUTH.goToRegister} className="text-slate-900 underline underline-offset-2">Create one</Link>.</p>

          <form onSubmit={submit} data-testid={AUTH.loginForm} className="mt-8 space-y-4">
            <div>
              <label className="text-overline">Email</label>
              <input
                type="email"
                required
                data-testid={AUTH.loginEmail}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full mt-1.5 bg-white border border-slate-200 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 focus:outline-none"
                placeholder="you@company.com"
              />
            </div>
            <div className="relative">
              <label className="text-overline flex items-center justify-between">
                <span>Password</span>
                <Link to="/forgot-password" data-testid="auth-forgot-link" className="text-[10px] font-mono uppercase tracking-wider text-slate-500 hover:text-slate-900 normal-case">Forgot?</Link>
              </label>
              <input
                type={showPassword ? "text" : "password"}
                required
                data-testid={AUTH.loginPassword}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full mt-1.5 bg-white border border-slate-200 rounded-md px-3 py-2.5 pr-10 text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 focus:outline-none"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                data-testid="auth-login-password-toggle"
                aria-label={showPassword ? "Hide password" : "Show password"}
                className="absolute right-3 top-[42px] text-slate-400 hover:text-slate-700"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid={AUTH.loginSubmit}
              className="w-full bg-slate-900 text-white rounded-md py-2.5 text-sm font-medium hover:bg-slate-800 disabled:opacity-60 inline-flex items-center justify-center gap-2"
            >
              {loading ? "Signing in..." : (<>Sign in <ArrowRight className="w-4 h-4" /></>)}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
